/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

// The arithmetic of a calculated field or table column:
//
//   qty * price          budget - items.amount        round(subtotal * 0.07, 2)
//   sum(items.amount)    count(items)                 max(0, budget - spent)
//
// The jobs service parses the same language the same way (Forms/Formula.cs)
// and its answer is the one printed; this one shows the answer as people type.

export type FormulaNode =
  | { kind: 'number'; value: number }
  | { kind: 'ref'; name: string; member?: string }
  | { kind: 'negate'; operand: FormulaNode }
  | { kind: 'binary'; op: '+' | '-' | '*' | '/'; left: FormulaNode; right: FormulaNode }
  | { kind: 'call'; name: string; args: FormulaNode[] };

export type FormulaRef = Extract<FormulaNode, { kind: 'ref' }>;

/** How names turn into numbers; null blanks the whole answer. */
export interface FormulaScope {
  value(ref: FormulaRef): number | null;
  /** count(table): rows with something typed in them. */
  count(ref: FormulaRef): number | null;
}

export const FORMULA_FUNCTIONS = ['sum', 'round', 'min', 'max', 'abs', 'count'];
export const MAX_FORMULA = 500;
const MAX_DEPTH = 40;

export class FormulaError extends Error {}

interface Token {
  kind: 'number' | 'name' | 'symbol';
  text: string;
}

function tokenize(text: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < text.length) {
    const c = text[i];
    if (/\s/.test(c)) {
      i++;
    } else if (/\d/.test(c)) {
      const match = /^\d+(\.\d+)?/.exec(text.slice(i)) as RegExpExecArray;
      tokens.push({ kind: 'number', text: match[0] });
      i += match[0].length;
    } else if (/[A-Za-z_]/.test(c)) {
      const match = /^[A-Za-z_][A-Za-z0-9_]*/.exec(text.slice(i)) as RegExpExecArray;
      tokens.push({ kind: 'name', text: match[0] });
      i += match[0].length;
    } else if ('+-*/(),.'.includes(c)) {
      tokens.push({ kind: 'symbol', text: c });
      i++;
    } else {
      throw new FormulaError(`unexpected '${c}'`);
    }
  }
  return tokens;
}

export function parseFormula(text: string | undefined | null): FormulaNode {
  if (!text?.trim()) {
    throw new FormulaError('empty');
  }
  if (text.length > MAX_FORMULA) {
    throw new FormulaError('too long');
  }
  const tokens = tokenize(text);
  let at = 0;
  const is = (symbol: string) => at < tokens.length && tokens[at].kind === 'symbol' && tokens[at].text === symbol;
  const expect = (symbol: string) => {
    if (!is(symbol)) {
      throw new FormulaError(`expected '${symbol}'`);
    }
    at++;
  };

  const expression = (depth: number): FormulaNode => {
    let node = term(depth);
    while (is('+') || is('-')) {
      const op = tokens[at++].text as '+' | '-';
      node = { kind: 'binary', op, left: node, right: term(depth) };
    }
    return node;
  };
  const term = (depth: number): FormulaNode => {
    let node = unary(depth);
    while (is('*') || is('/')) {
      const op = tokens[at++].text as '*' | '/';
      node = { kind: 'binary', op, left: node, right: unary(depth) };
    }
    return node;
  };
  const unary = (depth: number): FormulaNode => {
    if (depth > MAX_DEPTH) {
      throw new FormulaError('too deep');
    }
    if (is('-')) {
      at++;
      return { kind: 'negate', operand: unary(depth + 1) };
    }
    if (is('+')) {
      at++;
      return unary(depth + 1);
    }
    return primary(depth);
  };
  const primary = (depth: number): FormulaNode => {
    if (at >= tokens.length) {
      throw new FormulaError('unexpected end');
    }
    const token = tokens[at];
    if (token.kind === 'number') {
      at++;
      return { kind: 'number', value: Number(token.text) };
    }
    if (is('(')) {
      at++;
      const inner = expression(depth + 1);
      expect(')');
      return inner;
    }
    if (token.kind !== 'name') {
      throw new FormulaError(`unexpected '${token.text}'`);
    }
    at++;
    if (is('(')) {
      const name = token.text.toLowerCase();
      if (!FORMULA_FUNCTIONS.includes(name)) {
        throw new FormulaError(`unknown function '${token.text}'`);
      }
      at++;
      const args: FormulaNode[] = [];
      if (!is(')')) {
        args.push(expression(depth + 1));
        while (is(',')) {
          at++;
          args.push(expression(depth + 1));
        }
      }
      expect(')');
      return { kind: 'call', name, args };
    }
    if (is('.')) {
      at++;
      if (at >= tokens.length || tokens[at].kind !== 'name') {
        throw new FormulaError("expected a column after '.'");
      }
      return { kind: 'ref', name: token.text, member: tokens[at++].text };
    }
    return { kind: 'ref', name: token.text };
  };

  const node = expression(0);
  if (at < tokens.length) {
    throw new FormulaError(`unexpected '${tokens[at].text}'`);
  }
  return node;
}

export function tryParseFormula(text: string | undefined | null): FormulaNode | null {
  try {
    return parseFormula(text);
  } catch {
    return null;
  }
}

/** Every name a formula reads. */
export function formulaRefs(node: FormulaNode): FormulaRef[] {
  switch (node.kind) {
    case 'ref':
      return [node];
    case 'negate':
      return formulaRefs(node.operand);
    case 'binary':
      return [...formulaRefs(node.left), ...formulaRefs(node.right)];
    case 'call':
      return node.args.flatMap(formulaRefs);
    default:
      return [];
  }
}

/** Half away from zero, to a number of places, without 1.005 turning into 1.00. */
export function roundTo(value: number, places: number): number {
  const sign = value < 0 ? -1 : 1;
  const text = String(Math.abs(value));
  // shifting the decimal point in the text keeps 1.005 at 1.005, where * 100 makes it 100.49999
  const shifted = text.includes('e') ? Math.abs(value) * 10 ** places : Number(`${text}e${places}`);
  return (sign * Math.round(shifted)) / 10 ** places || 0;
}

/** The answer to satang, or null when a name cannot be read or something is divided by zero. */
export function evaluateFormula(node: FormulaNode, scope: FormulaScope): number | null {
  const value = evaluate(node, scope);
  return value === null || !Number.isFinite(value) ? null : roundTo(value, 2);
}

function evaluate(node: FormulaNode, scope: FormulaScope): number | null {
  switch (node.kind) {
    case 'number':
      return node.value;
    case 'ref':
      return scope.value(node);
    case 'negate': {
      const value = evaluate(node.operand, scope);
      return value === null ? null : -value;
    }
    case 'binary': {
      const left = evaluate(node.left, scope);
      const right = evaluate(node.right, scope);
      if (left === null || right === null) {
        return null;
      }
      switch (node.op) {
        case '+':
          return left + right;
        case '-':
          return left - right;
        case '*':
          return left * right;
        default:
          return right === 0 ? null : left / right;
      }
    }
    case 'call':
      return call(node.name, node.args, scope);
  }
}

function call(name: string, args: FormulaNode[], scope: FormulaScope): number | null {
  if (name === 'count') {
    return args.length === 1 && args[0].kind === 'ref' && !args[0].member ? scope.count(args[0]) : null;
  }
  const values = args.map((a) => evaluate(a, scope));
  if (values.some((v) => v === null)) {
    return null;
  }
  const numbers = values as number[];
  switch (name) {
    case 'sum':
      return numbers.reduce((a, b) => a + b, 0);
    case 'min':
      return numbers.length ? Math.min(...numbers) : null;
    case 'max':
      return numbers.length ? Math.max(...numbers) : null;
    case 'abs':
      return numbers.length === 1 ? Math.abs(numbers[0]) : null;
    case 'round':
      if (numbers.length === 1) {
        return roundTo(numbers[0], 0);
      }
      return numbers.length === 2 && Number.isInteger(numbers[1]) && numbers[1] >= 0 && numbers[1] <= 6 ? roundTo(numbers[0], numbers[1]) : null;
    default:
      return null;
  }
}
